<script setup lang="ts">
import type { MenuItem } from "primevue/menuitem";

import Menubar from "primevue/menubar";
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";

import { appVersion } from "@/appVersion";
import AccountDropdown from "@/components/AccountDropdown.vue";
import LoaderGlobal from "@/components/LoaderGlobal.vue";
import ThemeSwitch from "@/components/ThemeSwitch.vue";
import {
  canShowParentGroup,
  isRouteVisible,
} from "@/modules/permissions/composables/usePermissions";
import { useIdentityStore } from "@/stores/identity";

const router = useRouter();
const route = useRoute();
const IdentityStore = useIdentityStore();
const MenuItems = computed<MenuItem[]>(() => {
  return [
    {
      label: "About",
      route: {
        name: "about",
      },
      visible: !IdentityStore.isAuthenticated,
    },
    // Dashboard
    {
      label: "Dashboard",
      icon: "pi pi-clipboard",
      items: [
        {
          label: "Queues",
          icon: "pi pi-home",
          route: {
            name: "dashboard",
          },
          visible: IdentityStore.isAuthenticated,
        },
        {
          label: "Reports",
          icon: "pi pi-chart-bar",
          items: [
            {
              label: "Sales Microscope",
              icon: "pi pi-search-plus",
              route: {
                name: "reports-sales-microscope",
              },
              visible: isRouteVisible("reports-sales-microscope"),
            },
          ],
          visible: canShowParentGroup(["reports-sales-microscope"]),
        },
      ],
      visible: IdentityStore.isAuthenticated,
    },
    {
      label: "Manage",
      icon: "pi pi-database",
      items: [
        {
          label: "Products",
          icon: "pi pi-id-card",
          route: {
            name: "product-manager",
          },
          visible: isRouteVisible("product-manager"),
        },
        {
          label: "Inventory",
          icon: "pi pi-truck",
          route: {
            name: "inventory-manager",
          },
          visible: isRouteVisible("inventory-manager"),
        },
        {
          label: "Vendor Products",
          icon: "pi pi-th-large",
          route: {
            name: "vendor-product-manager",
          },
          visible: isRouteVisible("vendor-product-manager"),
        },
        // Brand
        // Vendors
        // Vendor Products
        // Categories
        // Attributes
        // {
        //   separator: true,
        // },
        // Warehouses
        // Purchase Orders
        // Inventory
        {
          separator: true,
        },
        {
          label: "Listings",
          icon: "pi pi-list",
          route: {
            name: "listing-manager",
          },
          visible: isRouteVisible("listing-manager"),
        },
        {
          separator: true,
        },
        {
          label: "Promotions",
          icon: "pi pi-tags",
          route: {
            name: "promotion-manager",
          },
          visible: isRouteVisible("promotion-manager"),
        },
        {
          separator: true,
        },
        {
          label: "Transfers",
          icon: "pi pi-truck",
          route: {
            name: "transfer-manager",
          },
          visible: isRouteVisible("transfer-manager"),
        },
        {
          separator: true,
        },
        {
          label: "Marketplaces",
          icon: "pi pi-globe",
          route: {
            name: "marketplace-manager",
          },
          visible: isRouteVisible("marketplace-manager"),
        },
        // adding rich content manager to the navigation
        {
          label: "Rich Content",
          icon: "pi pi-image",
          route: {
            name: "rich-content-manager",
          },
          visible: isRouteVisible("rich-content-manager"),
        },
        // Channels
        {
          label: "Channels",
          icon: "pi pi-sitemap",
          route: {
            name: "channels-manager",
          },
          visible: isRouteVisible("channels-manager"),
        },
        // Orders
        // Customers
        // Service Tickets
        // {
        //   separator: true,
        // },
        // Website Collections
        // Account
        // Integrations
      ],
      visible: canShowParentGroup([
        "product-manager",
        "inventory-manager",
        "vendor-product-manager",
        "listing-manager",
        "promotion-manager",
        "transfer-manager",
        "marketplace-manager",
        "rich-content-manager",
        "channels-manager",
      ]),
    },
    {
      label: "Admin",
      icon: "pi pi-pencil",
      items: [
        {
          label: "Schedule Listing Events",
          icon: "pi pi-calendar-clock",
          route: {
            name: "schedule-listing-events",
          },
          visible: isRouteVisible("schedule-listing-events"),
        },
        {
          label: "Amazon Competitive",
          icon: "pi pi-amazon",
          route: {
            name: "amazon-competitive-reporting",
          },
          visible: isRouteVisible("amazon-competitive-reporting"),
        },
      ],
      visible: canShowParentGroup(["schedule-listing-events", "amazon-competitive-reporting"]),
    },
    {
      label: "Login",
      route: {
        name: "login",
      },
      icon: "pi pi-user",
      visible: !IdentityStore.isAuthenticated,
    },
    {
      label: "User",
      icon: "pi pi-user",
      items: [
        {
          label: "User Settings",
          icon: "pi pi-user-edit",
          route: {
            name: "user-settings",
          },
          visible: isRouteVisible("user-settings"),
        },
        {
          label: "Logout",
          icon: "pi pi-user-minus",
          command: (): void => {
            if (IdentityStore.isAuthenticated) {
              IdentityStore.logout();
              if (route.path !== "/login" && (route.meta?.requiresAuth ?? false)) {
                router.push({
                  name: "login",
                  query: {
                    redirect: route.fullPath,
                  },
                });
              }
            }
          },
          visible: IdentityStore.isAuthenticated,
        },
      ],
      visible: IdentityStore.isAuthenticated,
    },
  ];
});
</script>

<template>
  <div class="main-navigation relative z-[25]">
    <Menubar
      class="rounded-none border-surface-200 bg-surface-50 dark:border-surface-800 dark:bg-surface-900"
      :model="MenuItems"
    >
      <template #start>
        <div class="flex items-center gap-2">
          <RouterLink to="/">
            <div class="text-md font-display cursor-pointer font-bold uppercase md:flex lg:text-xl">
              Bläckfisk
            </div>
          </RouterLink>
          <div
            class="rounded-lg bg-surface-150 px-2 py-0.5 text-sm text-surface-500 ring-1 ring-inset ring-surface-300 dark:bg-surface-750 dark:text-primary-500 dark:ring-primary-900"
          >
            {{ appVersion }}
          </div>
        </div>
      </template>
      <template #item="{ item, props }">
        <div class="p-menubar-item-content">
          <RouterLink v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
            <a :href="href" v-bind="props.action" class="p-menubar-item-link" @click="navigate">
              <span class="p-menubar-item-icon" :class="item.icon" />
              <span class="p-menubar-item-label">{{ item.label }}</span>
              <span
                v-if="(item?.items ?? []).length > 0"
                class="p-menubar-submenu-icon pi pi-caret-down"
              />
            </a>
          </RouterLink>
          <a
            v-else
            :href="item.url"
            :target="item.target"
            v-bind="props.action"
            class="p-menubar-item-link"
          >
            <span class="p-menubar-item-icon" :class="item.icon" />
            <span class="p-menubar-item-label">{{ item.label }}</span>
            <span
              v-if="(item?.items ?? []).length > 0"
              class="p-menubar-submenu-icon pi pi-caret-down"
            />
          </a>
        </div>
      </template>
      <template #end>
        <div class="flex">
          <AccountDropdown />
          <ThemeSwitch class="hidden w-min sm:flex" />
        </div>
      </template>
    </Menubar>
  </div>
  <LoaderGlobal />
</template>

<style lang="postcss" scoped></style>
